// ABOUTME: The apply flow's week as the talent edits it: the job's days with the times the talent gave, which of them
// ABOUTME: changed against the profile, and the days in the shape pro-core takes them (weekFit, apply, change my week).
import type {
  AvailabilityDay,
  TimeRange,
  Weekday
} from 'modules/availability/types';
import { rangeCovers } from 'modules/availability/utils';
import type { WeekFitAnswer, WeekFitRequest } from '../../types';

/** The talent's edits so far: a day's ranges as they now stand. Days not in here are the profile's. */
export type WeekEdits = Partial<Record<Weekday, TimeRange[]>>;

/**
 * The job's days with the edits laid over the profile's answer, as the request bodies carry them. Only the available
 * ranges are edited here; the preferred ones go back as the profile has them, so saving to the profile keeps them.
 *
 * On an edited day only the preferred ranges the new time still holds are sent. pro-core's apply v2 takes sent
 * preferred ranges as the talent's word and runs them through the availability PUT's validator, so one the edit left
 * outside would refuse the whole application (`preferred.outside-available`); pro-core itself drops exactly those when
 * it merges the profile's own, which is what this mirrors.
 */
export const editedDays = (
  answer: WeekFitAnswer | undefined,
  edits: WeekEdits
): AvailabilityDay[] =>
  (answer?.days ?? []).map(day => {
    const edited = edits[day.dayOfWeek];
    const preferred = day.yoursPreferred ?? [];
    return {
      dayOfWeek: day.dayOfWeek,
      available: edited ?? day.yours,
      preferred: edited
        ? preferred.filter(range =>
            edited.some(around => rangeCovers(around, range))
          )
        : preferred
    };
  });

export const hasEdits = (edits: WeekEdits): boolean =>
  Object.keys(edits).length > 0;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The weekFit body once the talent changed anything in the step: the edited days and the hours a week each once
 * touched, and always the start the step shows (pro-core #2119: the profile has no start of its own, so START_DATE can
 * only come from the start sent here). Its place in the query key re-checks on every change, so pro-core warns about
 * them (WEEKLY_HOURS, START_DATE) as they now stand. Undefined while nothing was touched: the profile's own answer
 * stands then, and its start, the job's (or today), never warns. A cleared date field counts as touched and sends no
 * start: the check is asked again (an empty body shares the profile check's key, so it comes from the cache).
 */
export const changedWeekRequest = (
  days: AvailabilityDay[],
  edits: WeekEdits,
  weeklyMinutes: number | undefined,
  startFrom: string | undefined,
  shownStart: string | undefined
): WeekFitRequest | undefined => {
  if (
    !hasEdits(edits) &&
    weeklyMinutes === undefined &&
    startFrom === undefined
  ) {
    return undefined;
  }
  return {
    ...(hasEdits(edits) ? { days } : {}),
    ...(weeklyMinutes !== undefined ? { weeklyMinutes } : {}),
    ...(shownStart && ISO_DAY.test(shownStart) ? { startFrom: shownStart } : {})
  };
};

/** "Mo, Mi, Fr": the days the talent has time on, for the review and for what the application shows later. */
export const daysWithTime = (days: AvailabilityDay[]): Weekday[] =>
  days.filter(day => day.available.length > 0).map(day => day.dayOfWeek);

const ORDER: Weekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY'
];

/** ISO weekday (Monday = 1) of a Weekday, for the short day names the feed already has. */
export const isoDayOf = (weekday: Weekday): number =>
  ORDER.indexOf(weekday) + 1;
